/**
 * RTL (Right-to-Left) utilities and helpers
 * Handles direction switching and logical CSS properties
 */

import { isRTL } from '@/core/i18n'

/**
 * Toggle document direction based on language
 */
export const setDocumentDirection = (language: string): void => {
  const direction = isRTL(language) ? 'rtl' : 'ltr'
  document.documentElement.dir = direction
  document.documentElement.lang = language
}

/**
 * Get current document direction
 */
export const getDocumentDirection = (): 'rtl' | 'ltr' => {
  return (document.documentElement.dir as 'rtl' | 'ltr') || 'ltr'
}

/**
 * Check if current direction is RTL
 */
export const isCurrentDirectionRTL = (): boolean => {
  return getDocumentDirection() === 'rtl'
}

/**
 * Logical CSS utilities for RTL support
 * These functions help convert directional properties to logical ones
 */
export const logical = {
  // Margin utilities
  marginStart: (value: string | number) => ({
    marginInlineStart: typeof value === 'number' ? `${value}px` : value,
  }),
  marginEnd: (value: string | number) => ({
    marginInlineEnd: typeof value === 'number' ? `${value}px` : value,
  }),
  
  // Padding utilities
  paddingStart: (value: string | number) => ({
    paddingInlineStart: typeof value === 'number' ? `${value}px` : value,
  }),
  paddingEnd: (value: string | number) => ({
    paddingInlineEnd: typeof value === 'number' ? `${value}px` : value,
  }),
  
  // Border utilities
  borderStart: (value: string) => ({
    borderInlineStart: value,
  }),
  borderEnd: (value: string) => ({
    borderInlineEnd: value,
  }),
  
  // Position utilities
  start: (value: string | number) => ({
    insetInlineStart: typeof value === 'number' ? `${value}px` : value,
  }),
  end: (value: string | number) => ({
    insetInlineEnd: typeof value === 'number' ? `${value}px` : value,
  }),
  
  // Text alignment
  textStart: () => ({
    textAlign: 'start' as const,
  }),
  textEnd: () => ({
    textAlign: 'end' as const,
  }),
}

/**
 * Get the appropriate icon rotation for RTL
 */
export const getRTLIconRotation = (baseRotation: number = 0): number => {
  return isCurrentDirectionRTL() ? 180 + baseRotation : baseRotation
}

/**
 * Get the appropriate transform for RTL
 */
export const getRTLTransform = (transform: string = ''): string => {
  if (isCurrentDirectionRTL()) {
    return `scaleX(-1) ${transform}`.trim()
  }
  return transform
}

/**
 * Tailwind CSS logical utilities
 * These can be used in className strings for RTL support
 */
export const tw = {
  // Margin
  ms: (size: string) => `ms-${size}`, // margin-inline-start
  me: (size: string) => `me-${size}`, // margin-inline-end
  
  // Padding
  ps: (size: string) => `ps-${size}`, // padding-inline-start
  pe: (size: string) => `pe-${size}`, // padding-inline-end
  
  // Border
  borderS: (size: string = '') => `border-s${size ? `-${size}` : ''}`, // border-inline-start
  borderE: (size: string = '') => `border-e${size ? `-${size}` : ''}`, // border-inline-end
  
  // Position
  start: (size: string) => `start-${size}`, // inset-inline-start
  end: (size: string) => `end-${size}`, // inset-inline-end
  
  // Text
  textStart: () => 'text-start',
  textEnd: () => 'text-end',
  
  // Rounded corners (for RTL-aware components)
  roundedS: (size: string = '') => `rounded-s${size ? `-${size}` : ''}`,
  roundedE: (size: string = '') => `rounded-e${size ? `-${size}` : ''}`,
}

/**
 * CSS-in-JS helper for creating RTL-aware styles
 */
export const createRTLStyles = (styles: {
  ltr?: React.CSSProperties
  rtl?: React.CSSProperties
  common?: React.CSSProperties
}): React.CSSProperties => {
  const { ltr = {}, rtl = {}, common = {} } = styles
  const currentStyles = isCurrentDirectionRTL() ? rtl : ltr
  
  return {
    ...common,
    ...currentStyles,
  }
}

/**
 * Class name helper for RTL-aware components
 */
export const createRTLClassName = (classes: {
  ltr?: string
  rtl?: string
  common?: string
}): string => {
  const { ltr = '', rtl = '', common = '' } = classes
  const directionalClass = isCurrentDirectionRTL() ? rtl : ltr
  
  return [common, directionalClass].filter(Boolean).join(' ')
} 